import { useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { IGiveawayAuto } from "../../../../../../interfaces";

import useAuth from "../../../../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { DateFns } from "../../../../../../utils/dateFns";
import { EmitError } from "../../../../../../utils/EmitError";

interface IValidateDatesProps {
    giveawayPeriodBeginning: Date;
    giveawayPeriodEnding: Date;
    giveawayDate: Date;
}

interface IProps {
    onSubmit: (giveawayAuto: IGiveawayAuto) => Promise<void>;
    giveawayAuto: IGiveawayAuto | undefined;
    onSubmitGiveawayDataStepFinished: () => void;
}

export default function useGiveawayDataStep({ giveawayAuto, onSubmit, onSubmitGiveawayDataStepFinished }: IProps) {
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);

    const { activeCity } = useAuth();
    const navigate = useNavigate();

    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        clearErrors,
        watch,
        setError
    } = useForm<IGiveawayAuto>({
        defaultValues: {
            description: giveawayAuto?.description || '',
            type: giveawayAuto?.type || 'Loteria Federal',
            period_beginning: giveawayAuto?.period_beginning ? giveawayAuto.period_beginning : '',
            period_ending: giveawayAuto?.period_ending ? giveawayAuto.period_ending : '',
            date: giveawayAuto?.date ? giveawayAuto.date : '',
            city_id: giveawayAuto?.city?.id ? giveawayAuto.city.id
                : activeCity ? activeCity.id
                    : undefined,
        }
    });

    const validateDates = useCallback(({
        giveawayPeriodBeginning,
        giveawayPeriodEnding,
        giveawayDate,
    }: IValidateDatesProps): boolean => {
        let hasDateValidationError = false;



        //validar se a data do sorteio é depois da data de fim do periodo
        if (!DateFns.dateIsAfter(giveawayDate, giveawayPeriodEnding)) {
            setError("date", { message: "Sorteio não pode ocorrer antes ou no mesmo dia do fim do período das notas!" });
            hasDateValidationError = true;
        }

        return hasDateValidationError;
    }, [setError]);

    const handleSubmitForm: SubmitHandler<IGiveawayAuto> = useCallback(async (data): Promise<boolean> => {
        try {
            setIsSubmiting(true);

            const giveawayPeriodBeginning = DateFns.dateStringToDateTime(DateFns.setTimeToMidnight(new Date(data.period_beginning)).toISOString());
            const giveawayPeriodEnding = DateFns.dateStringToDateTime(DateFns.setTimeToOneMillisecondBeforeMidnight(new Date(data.period_ending)).toISOString());
            const giveawayDate = DateFns.dateStringToDateTime(DateFns.setSecondsAndMillisecondsToZero(new Date(data.date)).toISOString());

            const hasDateValidationError = validateDates({
                giveawayPeriodBeginning,
                giveawayPeriodEnding,
                giveawayDate,
            });

            if (hasDateValidationError) {
                setIsSubmiting(false);
                return false;
            }

            await onSubmit({
                ...data,
                period_beginning: giveawayPeriodBeginning.toISOString(),
                period_ending: giveawayPeriodEnding.toISOString(),
                date: giveawayDate.toISOString(),
            });

            onSubmitGiveawayDataStepFinished();
            return true;
        } catch (err) {
            EmitError(err);
            return false;
        } finally {
            setIsSubmiting(false);
        }
    }, [onSubmit, onSubmitGiveawayDataStepFinished, validateDates]);

    const handleClickUpdateAndLeave: SubmitHandler<IGiveawayAuto> = useCallback(async (data) => {
        const success = await handleSubmitForm(data);

        if (success) {
            navigate('/sorteios/auto');
        }
    }, [handleSubmitForm, navigate]);

    const handleChangeDateRange = useCallback((values: any) => {
        if (!values) {
            setValue('period_beginning', '');
            setValue('period_ending', '');
            return;
        }

        const period_beginning = values[0];
        const period_ending = values[1];

        setValue('period_beginning', period_beginning);
        setValue('period_ending', period_ending);
        clearErrors('period_beginning');
        clearErrors('period_ending');
    }, [setValue, clearErrors]);

    return {
        handleSubmitForm,
        handleSubmit,
        control,
        isSubmiting,
        errors,
        setValue,
        watch,
        clearErrors,
        handleChangeDateRange,
        handleClickUpdateAndLeave
    }
}
